import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';
import AlertMessage from '../AlertMessage';
import Loading from '../Loading';
import Logo from '../../global/img/logo_full_white.png';
import './EmailVerification.css';

const EmailVerification = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');
    const user = useSelector(state => state.user);
    
    const [loading, setLoading] = useState(true);
    const [verified, setVerified] = useState(false);
    const [error, setError] = useState('');
    const [alert, setAlert] = useState({ show: false, type: '', message: '' });

    useEffect(() => {
        if (!token) {
            setError('Invalid verification link. No token provided.');
            setLoading(false);
            return;
        }

        verifyEmail();
    }, [token]);

    const verifyEmail = async () => {
        try {
            const response = await fetch('/verify-email', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token }),
            });

            const data = await response.json();

            if (response.ok) {
                setVerified(true);
                setAlert({
                    show: true,
                    type: 'success',
                    message: 'Email verified successfully! You can now access all features.'
                });
            } else {
                setError(data.message || 'Email verification failed. Please try again.');
                setAlert({
                    show: true,
                    type: 'danger',
                    message: data.message || 'Email verification failed. Please try again.'
                });
            }
        } catch (err) {
            setError('Network error. Please try again later.');
            setAlert({
                show: true,
                type: 'danger',
                message: 'Network error. Please try again later.'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleHomeRedirect = () => {
        navigate('/');
    };

    const handleResendVerification = async () => {
        try {
            // Get user email from Redux state
            if (!user || !user.email) {
                setError('User information not available. Please try logging in again.');
                return;
            }

            const userEmail = user.email;
            
            // Call resend verification API
            const resendResponse = await fetch('/resend-email-verification', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email: userEmail }),
            });

            const resendData = await resendResponse.json();

            if (resendResponse.ok) {
                setAlert({
                    show: true,
                    type: 'success',
                    message: `Verification email sent to ${userEmail}. Please check your inbox.`
                });
            } else {
                setAlert({
                    show: true,
                    type: 'danger',
                    message: resendData.message || 'Failed to resend verification email'
                });
            }
        } catch (err) {
            setAlert({
                show: true,
                type: 'danger',
                message: 'Network error. Please try again later.'
            });
        }
    };

    if (loading) {
        return (
            <div id='fullHeight'>
                <div id='loading' />
            </div>
        );
    }

    return (
        <div>
            <section className='emailVerificationSection'>
                <div className='emailVerificationContainer'>
                    <img src={Logo} alt='Inside Injuries Logo' className='verificationLogo' />
                    
                    {verified ? (
                        <div className='verificationContent success'>
                            <h1 className='verificationHeader'>Email Verified Successfully!</h1>
                            <p className='verificationMessage'>
                                Your email has been verified. You now have full access to all features and content.
                            </p>
                            
                            <div className='verificationActions'>
                                <Button 
                                    variant='primary'
                                    className='verificationButton'
                                    onClick={handleHomeRedirect}
                                >
                                    Go to Home
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className='verificationContent error'>
                            <h1 className='verificationHeader'>Verification Failed</h1>
                            <p className='verificationMessage error'>
                                {error}
                            </p>
                            
                            <div className='verificationActions'>
                                <Button 
                                    variant='secondary'
                                    className='verificationButton me-3'
                                    onClick={handleResendVerification}
                                >
                                    Resend Verification Email
                                </Button>
                                <Button 
                                    variant='outline-primary'
                                    className='verificationButton'
                                    onClick={handleHomeRedirect}
                                >
                                    Back to Home
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {alert.show && (
                <AlertMessage
                    type={alert.type}
                    message={alert.message}
                    onClose={() => setAlert({ ...alert, show: false })}
                />
            )}
        </div>
    );
};

export default EmailVerification;
